import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"

// Initial state
const initialState = {
  overview: {
    totalProjects: 0,
    activeProjects: 0,
    totalSites: 0,
    pendingWorks: 0,
    criticalObservations: 0,
    totalFiles: 0,
  },
  recentActivities: [],
  projectSummaries: [],
  workSummaries: [],
  observationSummaries: [],
  charts: {
    projectsByStatus: [],
    worksByType: [],
    observationsBySeverity: [],
    monthlyProgress: [],
  },
  loading: false,
  error: null,
  lastUpdated: null,
}

// Async thunks for dashboard data
/** @type {import("@reduxjs/toolkit").AsyncThunk<any, void, {}>} */
export const fetchDashboardOverview = createAsyncThunk(
  "dashboard/fetchDashboardOverview",
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetch("/dashboard/overview", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      })

      if (!response.ok) {
        const error = await response.json()
        return rejectWithValue(error.message || "Failed to fetch dashboard overview")
      }

      const data = await response.json()
      return data.data
    } catch (error) {
      return rejectWithValue(error.message || "Network error")
    }
  },
)

/** @type {import("@reduxjs/toolkit").AsyncThunk<any, Record<string, unknown> | void, {}>} */
export const fetchRecentActivities = createAsyncThunk(
  "dashboard/fetchRecentActivities",
  async (limit = 10, { rejectWithValue }) => {
    try {
      const response = await fetch(`/dashboard/activities?limit=${limit}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      })

      if (!response.ok) {
        const error = await response.json()
        return rejectWithValue(error.message || "Failed to fetch recent activities")
      }

      const data = await response.json()
      return data.data
    } catch (error) {
      return rejectWithValue(error.message || "Network error")
    }
  },
)

/** @type {import("@reduxjs/toolkit").AsyncThunk<any, void, {}>} */
export const fetchProjectSummaries = createAsyncThunk(
  "dashboard/fetchProjectSummaries",
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetch("/dashboard/projects", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      })

      if (!response.ok) {
        const error = await response.json()
        return rejectWithValue(error.message || "Failed to fetch project summaries")
      }

      const data = await response.json()
      return data.data
    } catch (error) {
      return rejectWithValue(error.message || "Network error")
    }
  },
)

/** @type {import("@reduxjs/toolkit").AsyncThunk<any, void, {}>} */
export const fetchWorkSummaries = createAsyncThunk("dashboard/fetchWorkSummaries", async (_, { rejectWithValue }) => {
  try {
    const response = await fetch("/dashboard/works", {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    })

    if (!response.ok) {
      const error = await response.json()
      return rejectWithValue(error.message || "Failed to fetch work summaries")
    }

    const data = await response.json()
    return data.data
  } catch (error) {
    return rejectWithValue(error.message || "Network error")
  }
})

/** @type {import("@reduxjs/toolkit").AsyncThunk<any, void, {}>} */
export const fetchObservationSummaries = createAsyncThunk(
  "dashboard/fetchObservationSummaries",
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetch("/dashboard/observations", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      })

      if (!response.ok) {
        const error = await response.json()
        return rejectWithValue(error.message || "Failed to fetch observation summaries")
      }

      const data = await response.json()
      return data.data
    } catch (error) {
      return rejectWithValue(error.message || "Network error")
    }
  },
)

/** @type {import("@reduxjs/toolkit").AsyncThunk<any, void, {}>} */
export const fetchDashboardCharts = createAsyncThunk(
  "dashboard/fetchDashboardCharts",
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetch("/dashboard/charts", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      })

      if (!response.ok) {
        const error = await response.json()
        return rejectWithValue(error.message || "Failed to fetch dashboard charts")
      }

      const data = await response.json()
      return data.data
    } catch (error) {
      return rejectWithValue(error.message || "Network error")
    }
  },
)

// Refresh all dashboard data
/** @type {import("@reduxjs/toolkit").AsyncThunk<any, void, {}>} */
export const refreshDashboard = createAsyncThunk("dashboard/refreshDashboard", async (_, { dispatch }) => {
  try {
    await Promise.all([
      dispatch(fetchDashboardOverview()),
      dispatch(fetchRecentActivities()),
      dispatch(fetchProjectSummaries()),
      dispatch(fetchWorkSummaries()),
      dispatch(fetchObservationSummaries()),
      dispatch(fetchDashboardCharts()),
    ])

    return new Date().toISOString()
  } catch (error) {
    throw error
  }
})

// Dashboard slice
const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    updateOverviewCount: (state, action) => {
      const { field, value } = action.payload
      if (state.overview.hasOwnProperty(field)) {
        state.overview[field] = value
      }
    },
    addRecentActivity: (state, action) => {
      state.recentActivities.unshift(action.payload)
      // Keep only the latest 20 activities
      if (state.recentActivities.length > 20) {
        state.recentActivities = state.recentActivities.slice(0, 20)
      }
    },
    clearRecentActivities: (state) => {
      state.recentActivities = []
    },
    setLastUpdated: (state) => {
      state.lastUpdated = new Date().toISOString()
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch dashboard overview cases
      .addCase(fetchDashboardOverview.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchDashboardOverview.fulfilled, (state, action) => {
        state.loading = false
        state.overview = action.payload
        state.error = null
        state.lastUpdated = new Date().toISOString()
      })
      .addCase(fetchDashboardOverview.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Fetch recent activities cases
      .addCase(fetchRecentActivities.fulfilled, (state, action) => {
        state.recentActivities = action.payload
      })

      // Fetch project summaries cases
      .addCase(fetchProjectSummaries.fulfilled, (state, action) => {
        state.projectSummaries = action.payload
      })

      // Fetch work summaries cases
      .addCase(fetchWorkSummaries.fulfilled, (state, action) => {
        state.workSummaries = action.payload
      })

      // Fetch observation summaries cases
      .addCase(fetchObservationSummaries.fulfilled, (state, action) => {
        state.observationSummaries = action.payload
      })

      // Fetch dashboard charts cases
      .addCase(fetchDashboardCharts.fulfilled, (state, action) => {
        state.charts = action.payload
      })

      // Refresh dashboard cases
      .addCase(refreshDashboard.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(refreshDashboard.fulfilled, (state, action) => {
        state.loading = false
        state.lastUpdated = action.payload
        state.error = null
      })
      .addCase(refreshDashboard.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
  },
})

export const { clearError, updateOverviewCount, addRecentActivity, clearRecentActivities, setLastUpdated } =
  dashboardSlice.actions

export default dashboardSlice.reducer
